import React, { ReactNode, useRef } from 'react';
import { Box, Label } from '@apia/theme';
import { TApiaFilter, TApiaFilterValue } from '../../../../../../types';
import { useResponsiveTableContext } from '../../ResponsiveTableContext';
import store from '../../../../../../store';
import { formatMessage } from '../../../../../../utils/util';
import ApiaFilter from '../../../../ApiaFilter';
import { RangeFilter } from '../RangeFilter';
import DateToFilterRender from './DateToFilterRender';
import controlDatesOrder from './controlDatesOrder';
import RequiredBox from '../../../../../apia/execution/common/RequiredBox';
import { usePrevious } from '@apia/util';

const Filter = ({ filter }: { filter: TApiaFilter }) => {
  const {
    onFilterBlur,
    onFilterChange,
    onFilterError: onError,
    onFilterPressEnter,
    name,
  } = useResponsiveTableContext();
  const lastEmittedValue = React.useRef<TApiaFilterValue | null>(null);
  const lastEmittedValueByPartner = React.useRef<TApiaFilterValue | null>(null);

  const previousDeleteTimestamp = usePrevious(filter.deleteFiltersTimestamp);
  if (previousDeleteTimestamp.current !== filter.deleteFiltersTimestamp) {
    lastEmittedValue.current = filter.currentValue;
    lastEmittedValueByPartner.current = filter.filterToValue!;
  }
  const previousName = usePrevious(name);
  if (previousName.current !== name) {
    lastEmittedValue.current = filter.currentValue;
    lastEmittedValueByPartner.current = filter.filterToValue!;
  }

  const boxClassName =
    filter.type === 'D' ||
    filter.type === 'date' ||
    filter.type === 'triple' ||
    filter.type === 'dateHour' ||
    filter.isMeta ||
    filter.twoColumns
      ? 'twoColumns'
      : '';
  const isDate = filter.type === 'D' || filter.type === 'date';

  const column = store
    .getState()
    .responsiveTableSlice[
      name
    ].columns.find((current) => current.name === filter.column);
  const filterTitle =
    filter.title ||
    column?.label ||
    column?.title ||
    column?.name ||
    'noFilterNorColumnTitle';
  const labelFromTitle = isDate
    ? window.LBL_DATE_FILTER_FROM_TOOLTIP
    : filterTitle;
  const fromLabel =
    isDate && !filter.hideToFilter
      ? formatMessage(window.LBL_DATE_FILTER_FROM_TXT, {
          TOK1: filterTitle,
        })
      : filterTitle;
  const labelToTitle = isDate ? window.LBL_DATE_FILTER_TO_TOOLTIP : filterTitle;
  const toLabel =
    isDate && !filter.hideToFilter
      ? formatMessage(window.LBL_DATE_FILTER_TO_TXT, {
          TOK1: filterTitle,
        })
      : filterTitle;

  const filterOnChange = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      const result = controlDatesOrder(
        currentValue as string,
        lastEmittedValueByPartner.current as string,
      );
      if (result === true) {
        lastEmittedValue.current = currentValue;
        if (onFilterChange) {
          if (filter.type === 'D' || filter.type === 'date') {
            if (onError && filter.error) {
              onError({
                ...filter,
                error: undefined,
              });
            } else if (onError && filter.errorTo) {
              onError({
                ...filter,
                errorTo: undefined,
              });
            } else if (onError && filter.errorFrom) {
              onError({
                ...filter,
                errorFrom: undefined,
              });
            }
          }
          void onFilterChange({
            ...filter,
            error: undefined,
            errorFrom: undefined,
            errorTo: undefined,
            currentValue,
          });
        }
      } else {
        if (onError) {
          onError({ ...filter, error: result });
        }
      }
      return result;
    },
    [filter, onError, onFilterChange],
  );
  const filterOnBlur = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      if (onFilterBlur)
        void onFilterBlur({
          ...filter,
          currentValue,
        });
    },
    [filter, onFilterBlur],
  );
  const filterOnPressEnter = React.useCallback(
    (currentValue: TApiaFilterValue) => {
      if (onFilterPressEnter)
        void onFilterPressEnter({
          ...filter,
          currentValue,
        });
    },
    [filter, onFilterPressEnter],
  );
  const dateFilterOnError = React.useCallback(
    (error: string, dateType?: 'from' | 'to') => {
      if (onError && !dateType) {
        onError({ ...filter, error: error });
      } else if (onError && dateType === 'from') {
        onError({ ...filter, errorFrom: error });
      } else if (onError && dateType === 'to') {
        onError({ ...filter, errorTo: error });
      }
    },
    [filter, onError],
  );
  const actualFilter = useRef(filter);

  const renderFilter = React.useMemo(() => {
    let result: ReactNode = null;

    if (filter.isRange) result = <RangeFilter filter={filter} />;
    else if (
      filter.type === 'dateHour' &&
      filter.id === filter.groupedInputFirstId
    ) {
      result = (
        <Box sx={{ display: 'flex' }}>
          <ApiaFilter
            filter={filter}
            onChange={filterOnChange}
            onBlur={filterOnBlur}
            onPressEnter={filterOnPressEnter}
            shouldShowRedBorderOnRequired
          />
        </Box>
      );
    } else if (
      filter.type === 'dateHour' &&
      filter.id !== filter.groupedInputFirstId
    ) {
      result = null;
    } else if (
      filter.type === 'triple' &&
      filter.id === filter.groupedInputFirstId
    ) {
      result = (
        <Box className="tripleContainer">
          <ApiaFilter
            shouldShowRedBorderOnRequired
            filter={filter}
            onChange={filterOnChange}
            onBlur={filterOnBlur}
            onPressEnter={filterOnPressEnter}
          />
        </Box>
      );
    } else if (
      filter.type === 'triple' &&
      filter.id !== filter.groupedInputFirstId
    ) {
      result = null;
    } else if (filter.type === 'D' || filter.type === 'date') {
      actualFilter.current = {
        ...filter,
        required:
          filter.dateRequired ||
          filter.dateFromRequired ||
          filter.dateToRequired,
      };

      result = (
        <ApiaFilter
          filter={{
            ...filter,
            required:
              filter.dateRequired ||
              filter.dateFromRequired ||
              filter.dateToRequired,
          }}
          onChange={filterOnChange}
          onBlur={filterOnBlur}
          onPressEnter={filterOnPressEnter}
          onError={dateFilterOnError}
          shouldShowRedBorderOnRequired
        />
      );
    } else {
      result = (
        <ApiaFilter
          filter={filter}
          onChange={filterOnChange}
          onBlur={filterOnBlur}
          onPressEnter={filterOnPressEnter}
          shouldShowRedBorderOnRequired
        />
      );
    }

    return result;
  }, [
    dateFilterOnError,
    filter,
    filterOnBlur,
    filterOnChange,
    filterOnPressEnter,
  ]);

  if (!renderFilter || filter.hide) return null;
  const labelNode = <Box as="span">{fromLabel}</Box>;
  const labelToNode = <Box as="span">{toLabel}</Box>;

  return (
    <React.Fragment key={filter.id ?? (filter.column as string)}>
      <Label
        className={`${boxClassName} filter  filter__${filter.column} ${isDate ? 'from' : ''}`}
        title={labelFromTitle}
      >
        {filterTitle !== 'noFilterNorColumnTitle' && (
          <RequiredBox
            labelNode={labelNode}
            required={
              filter.type === 'D' || filter.type === 'date'
                ? actualFilter.current.required &&
                  (actualFilter.current.dateFromRequired ||
                    actualFilter.current.dateRequired)
                : filter.required
            }
            avoidColon
          />
        )}
        {renderFilter}
      </Label>
      {isDate && !actualFilter.current.hideToFilter && (
        <Label
          className={`${boxClassName} filter filter__${filter.column} to`}
          title={labelToTitle}
        >
          {filterTitle !== 'noFilterNorColumnTitle' && (
            <RequiredBox
              labelNode={labelToNode}
              required={
                (actualFilter.current.dateRequired ||
                  actualFilter.current.dateToRequired) &&
                actualFilter.current.required
              }
              avoidColon
            />
          )}
          <DateToFilterRender
            filter={{
              ...actualFilter.current,
              required:
                actualFilter.current.dateRequired ||
                actualFilter.current.dateToRequired ||
                actualFilter.current.dateFromRequired,
            }}
            filterTitle={filterTitle}
            lastEmittedValue={lastEmittedValue}
            lastEmittedValueByPartner={lastEmittedValueByPartner}
            avoidLabel={true}
          />
        </Label>
      )}
    </React.Fragment>
  );
};

export default Filter;
